import { Badge, Button, Input } from "@qn/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fingerprint, Trash2 } from "lucide-react";
import { useState } from "react";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { addPasskey, deviceName, type PasskeyRow, passkeysSupported } from "../../lib/passkeys";
import { Section } from "./Section";

export function PasskeysSection() {
  const t = useT();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["passkeys"],
    queryFn: () => api<{ passkeys: PasskeyRow[] }>("/auth/passkeys"),
  });
  const [name, setName] = useState("");
  const add = useMutation({
    mutationFn: () => addPasskey(name.trim() || deviceName()),
    onSuccess: () => {
      setName("");
      void qc.invalidateQueries({ queryKey: ["passkeys"] });
      void qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      api(`/auth/passkeys/${encodeURIComponent(id)}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["passkeys"] });
      void qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
  const rows = list.data?.passkeys ?? [];
  return (
    <Section title={t("passkeys.title")} hint={t("passkeys.hint")}>
      {!passkeysSupported() ? (
        <p className="text-sm text-[var(--fg-muted)]">{t("passkeys.unsupported")}</p>
      ) : (
        <div className="space-y-4">
          {rows.length > 0 && (
            <ul className="space-y-2">
              {rows.map((p) => (
                <li
                  key={p.id}
                  className="flex items-center gap-3 rounded-xl border border-[var(--line)] bg-[var(--surface-2)] p-3"
                >
                  <Fingerprint className="size-5 text-[var(--accent-text)]" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium" dir="auto">
                      {p.name}
                    </div>
                    <div className="text-xs text-[var(--fg-muted)]">
                      {new Date(p.created_at).toLocaleDateString()}
                      {p.backed_up ? ` · ${t("passkeys.synced")}` : ""}
                    </div>
                  </div>
                  {p.last_used_at && <Badge tone="success">{t("passkeys.used")}</Badge>}
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={t("action.delete")}
                    onClick={() => remove.mutate(p.id)}
                  >
                    <Trash2 className="size-4 text-[var(--danger)]" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("passkeys.namePlaceholder")}
              aria-label={t("passkeys.namePlaceholder")}
              maxLength={60}
              dir="auto"
            />
            <Button variant="primary" loading={add.isPending} onClick={() => add.mutate()}>
              <Fingerprint className="size-4" /> {t("passkeys.add")}
            </Button>
          </div>
          {(add.error || remove.error) && (
            <p role="alert" className="text-sm text-[var(--danger)]" dir="auto">
              {(add.error ?? remove.error)?.message}
            </p>
          )}
        </div>
      )}
    </Section>
  );
}
